import { ALEXANDRIA_METRICS } from 'dashboard409a/services/alexandria/types';
import { Medians, MetricValue } from 'dashboard409a/services/types';
import { COMPUTED_METRICS } from '../constants';
import { computeMultipleSeries, MetricSeriesByCompany, SeriesPoint, statWithMoM, StatWithMoM } from './metrics';

// Alexandria returns ratio metrics already as percent values (75.4 means
// 75.4%), so they flow to the cards unscaled. Missing data stays null —
// MetricCard renders it as "N/A" instead of a fabricated zero.

export const extractMetric = (byCompany: MetricSeriesByCompany, metricKey: string): Record<string, SeriesPoint[]> => {
  const result: Record<string, SeriesPoint[]> = {};
  Object.entries(byCompany).forEach(([companyId, metrics]) => {
    const series = metrics[metricKey];
    if (series?.length) {
      result[companyId] = series;
    }
  });
  return result;
};

// Multiple cards ("4.6x") label the delta "% month over month": relative
// change of the median.
const toMultipleValue = (stat: StatWithMoM): MetricValue => ({
  value: stat.current,
  change:
    stat.current !== null && stat.previous !== null && stat.previous !== 0
      ? ((stat.current - stat.previous) / Math.abs(stat.previous)) * 100
      : null,
  current_date: stat.currentDate,
  previous_date: stat.previousDate,
});

// Percent cards ("75.4%") label the delta "points month over month": the
// absolute percentage-point difference, not relative change.
const toPercentValue = (stat: StatWithMoM): MetricValue => ({
  value: stat.current,
  change: stat.current !== null && stat.previous !== null ? stat.current - stat.previous : null,
  current_date: stat.currentDate,
  previous_date: stat.previousDate,
});

// Builds the exact Medians shape MetricCardsRow renders: median across the
// comp set today, plus month-over-month movement of that median.
export const buildMedians = (byCompany: MetricSeriesByCompany): Medians => ({
  ntm_revenue_multiple: toMultipleValue(statWithMoM(computeMultipleSeries(byCompany, ALEXANDRIA_METRICS.NTM_REVENUE))),
  ltm_revenue_multiple: toMultipleValue(statWithMoM(computeMultipleSeries(byCompany, ALEXANDRIA_METRICS.LTM_REVENUE))),
  ntm_revenue_growth: toPercentValue(statWithMoM(extractMetric(byCompany, COMPUTED_METRICS.NTM_REVENUE_GROWTH))),
  ltm_revenue_growth: toPercentValue(statWithMoM(extractMetric(byCompany, ALEXANDRIA_METRICS.LTM_REVENUE_GROWTH))),
  gross_margin: toPercentValue(statWithMoM(extractMetric(byCompany, ALEXANDRIA_METRICS.GROSS_MARGIN))),
  ebitda_margin: toPercentValue(statWithMoM(extractMetric(byCompany, ALEXANDRIA_METRICS.EBITDA_MARGIN))),
});
